import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TopNav } from "../components/TopNav";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Activity, Tv, RefreshCw, Flame, ArrowRight } from "lucide-react";
import { API } from "../App";
import { formatLiveMinute } from "../lib/liveMinute";
import { countryToFlag } from "../lib/countryFlag";
import { LiveOnBetwayBadge } from "../components/LiveOnBetwayBadge";

/**
 * LiveNowPage — Iter 108
 *
 * Dedicated page listing every match that is currently in-play.  This
 * is the deep-link target for the new top-nav "Live" entry.  Polls
 * `/api/live/now-playing` every 60 s so the list stays fresh without
 * the user reloading.  Each card links to its full live tracker
 * (`/live/{fixtureId}`).
 */
const StatusPill = ({ status, minute, minuteExtra }) => {
  const isLive = ["1H", "2H", "ET", "P", "LIVE"].includes(status);
  const isHT = status === "HT";
  const inStoppage = isLive && typeof minuteExtra === "number" && minuteExtra > 0;
  const label = isHT
    ? "HT"
    : isLive
    ? (formatLiveMinute(minute, minuteExtra, status) || "'")
    : status;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full text-[10px] font-bold tracking-[0.16em] uppercase ${
        inStoppage
          ? "bg-amber-500/20 text-amber-300 border border-amber-500/50"
          : isHT
          ? "bg-amber-500/15 text-amber-500 border border-amber-500/30"
          : isLive
          ? "bg-rose-500/15 text-rose-500 border border-rose-500/30"
          : "bg-muted text-muted-foreground border border-border"
      }`}
      title={inStoppage ? `Stoppage time — ${minuteExtra} added minute${minuteExtra === 1 ? "" : "s"}` : undefined}
      data-testid="livepage-status-pill"
    >
      {isLive && (
        <span className="relative flex w-1.5 h-1.5">
          <span className={`absolute inline-flex w-full h-full rounded-full ${inStoppage ? "bg-amber-300" : "bg-rose-500"} opacity-75 animate-ping`} />
          <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${inStoppage ? "bg-amber-300" : "bg-rose-500"}`} />
        </span>
      )}
      {label}
    </span>
  );
};

const LiveNowPage = () => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    try {
      const r = await axios.get(`${API}/live/now-playing${force ? "?force=true" : ""}`);
      setMatches(r.data?.matches || []);
    } catch {
      /* keep last data on error */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => load(false), 60_000);
    return () => clearInterval(id);
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    load(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-1.5 inline-flex items-center gap-1.5">
              <Activity className="w-3 h-3" />
              {t("predictions.nowPlayingEyebrow") || "Live · polls every 60s"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              {t("predictions.nowPlaying") || "Now Playing"}
              <span className="ml-3 text-base font-medium text-muted-foreground" data-testid="livepage-count">
                {matches.length} {matches.length === 1 ? "match" : "matches"}
              </span>
            </h1>
          </div>
          <Button
            onClick={onRefresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="h-9 rounded-full px-3.5 border-border bg-card hover:bg-muted text-foreground"
            data-testid="livepage-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <Card className="border-border" data-testid="livepage-loading">
            <CardContent className="p-10 text-center text-muted-foreground">
              <Activity className="w-5 h-5 animate-pulse mx-auto mb-2" />
              Loading live fixtures…
            </CardContent>
          </Card>
        ) : matches.length === 0 ? (
          <Card className="border-border" data-testid="livepage-empty">
            <CardContent className="p-10 text-center">
              <Tv className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-display text-xl font-semibold mb-1">
                {t("predictions.noLive") || "No live matches right now"}
              </h3>
              <p className="text-sm text-muted-foreground">
                Check back in a few minutes — the list refreshes automatically.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="livepage-grid">
            {matches.map((m) => (
              <Link
                key={m.fixture_id}
                to={`/live/${m.fixture_id}`}
                className="group"
                data-testid={`livepage-card-${m.fixture_id}`}
              >
                <Card className="border-border h-full transition-colors group-hover:border-foreground/40">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground truncate font-medium inline-flex items-center gap-1">
                        <span aria-hidden>{countryToFlag(m.league?.country)}</span>
                        {m.league?.name}
                      </span>
                      <StatusPill status={m.status} minute={m.minute} minuteExtra={m.minute_extra} />
                    </div>

                    {m.betway_available && (
                      <LiveOnBetwayBadge compact />
                    )}

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-base font-semibold">
                        <span className="truncate pr-2">{m.home_team?.name}</span>
                        <span className="font-mono text-lg">{m.score?.home ?? 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-base font-semibold">
                        <span className="truncate pr-2">{m.away_team?.name}</span>
                        <span className="font-mono text-lg">{m.score?.away ?? 0}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <Badge className="bg-muted text-foreground border border-border text-[10px]">
                        <Flame className="w-2.5 h-2.5 mr-1 text-rose-400" />
                        LMI {m.lmi?.combined ?? "—"}
                      </Badge>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground group-hover:translate-x-0.5 transition-transform">
                        Open
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveNowPage;
